import type { CompetencyCode, KnowledgeCode, Question } from '../types'
import { SeedGenerator } from './generators/math_generators'

type Chapter1Seed = {
  id: string
  r: number
  c: number
  a: number
  b: number
  q: number
  profitVertex: number
  profitStep: number
  profitScale: number
  boxSide: number
  marginP: number
  marginQ: number
  marginK: number
}

const option = (id: string, text: string) => ({ id, text })

/** Prevent a seeded distractor from ever collapsing onto the keyed answer. */
const numericChoices = (correct: number, distractors: number[], suffix = '') => {
  const used = new Set<number>()
  return [correct, ...distractors].map((value, index) => {
    let candidate = value
    const bump = Math.max(1, index + 1)
    while (used.has(candidate)) candidate += bump
    used.add(candidate)
    return option(['A', 'B', 'C', 'D'][index], `${candidate}${suffix}`)
  })
}

const baseMeta: Pick<Question, 'exam' | 'domain' | 'prerequisites' | 'misconceptionOptions'> = {
  exam: 'VACT',
  domain: 'Toán học',
  prerequisites: ['functions'],
  misconceptionOptions: ['concept-gap', 'calculation-slip', 'strategy-choice', 'careless-reading'],
}

const functionBase = (seed: Chapter1Seed, topic: string, competency: CompetencyCode[] = ['quantitative-reasoning', 'problem-solving']) => ({
  ...baseMeta,
  knowledge: ['functions'] as KnowledgeCode[],
  competency,
  topic,
  tags: ['original', 'authored-variant', 'chapter-1', 'functions', 'conictypst-ch1', 'parameterized'],
  aiContext: JSON.stringify(seed),
})

const modelBase = (seed: Chapter1Seed, topic: string) => ({
  ...baseMeta,
  knowledge: ['functions', 'modelling'] as KnowledgeCode[],
  competency: ['modelling', 'decision-making', 'problem-solving'] as CompetencyCode[],
  topic,
  tags: ['original', 'authored-variant', 'chapter-1', 'functions', 'modelling', 'conictypst-ch1', 'parameterized'],
  aiContext: JSON.stringify(seed),
})

/**
 * The seeds use only exact extrema / optimum data. They are not random prose
 * substitutions: each one locks the values, answer key and full explanation
 * together for a persisted practice attempt.
 */
export function generateChapter1Seed(id: string): Chapter1Seed {
  const gen = new SeedGenerator(id)
  const r = [1, 2, 3][gen.randomInt(0, 2)]
  const q = [2, 3, 4, 5][gen.randomInt(0, 3)]
  const a = gen.randomInt(-3, 3)
  const b = gen.randomInt(-3, 4)
  const profitVertex = [30, 36, 42, 48, 54][gen.randomInt(0, 4)]
  const profitSteps = [5, 6, 7, 8]
  let profitStep = profitSteps[gen.randomInt(0, profitSteps.length - 1)]
  // Preserve the intended discrete-optimisation tension: the continuous
  // vertex is never itself an allowed lot size.
  if (profitVertex % profitStep === 0) profitStep = profitSteps.find(step => profitVertex % step !== 0) ?? 5
  const profitScale = [2, 3, 4][gen.randomInt(0, 2)]
  const boxSide = [24, 30, 36, 42][gen.randomInt(0, 3)]
  const marginP = [1, 2, 3][gen.randomInt(0, 2)]
  const marginQ = [2, 3, 4][gen.randomInt(0, 2)]
  const marginK = [6, 8, 10][gen.randomInt(0, 2)]
  return { id, r, c: gen.randomInt(-5, 5), a, b, q, profitVertex, profitStep, profitScale, boxSide, marginP, marginQ, marginK }
}

function cubicQuestions(seed: Chapter1Seed): Question[] {
  const { r, c } = seed
  const lower = c - 2 * r ** 3
  const upper = c + 2 * r ** 3
  const integerLevels = upper - lower - 1
  const minSlope = -3 * r * r
  return [
    {
      ...functionBase(seed, 'Hàm bậc ba – mức giá trị và cực trị', ['pattern-recognition', 'logical-reasoning', 'problem-solving']),
      id: `VACT-CH1-CUBIC-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Mức giá trị của hàm bậc ba',
      passageText: `Xét $f(x)=x^3-3\\cdot${r ** 2}x${c >= 0 ? '+' : ''}${c}$. Hai mức cực trị của đồ thị lần lượt là $${lower}$ và $${upper}$.`,
      stem: 'Có bao nhiêu số nguyên $m$ để phương trình $f(x)=m$ có ba nghiệm thực phân biệt?',
      choices: [option('A', `${integerLevels - 2}`), option('B', `${integerLevels}`), option('C', `${integerLevels + 1}`), option('D', `${integerLevels + 2}`)],
      answer: 'B',
      solution: `Đường thẳng $y=m$ cắt đồ thị bậc ba tại ba điểm phân biệt khi và chỉ khi nó nằm nghiêm ngặt giữa hai mức cực trị: $${lower}<m<${upper}$. Vì hai đầu mút đều nguyên, số giá trị nguyên là $${upper}-${lower}-1=${integerLevels}$.`,
      hint1: 'Chuyển bài toán về số giao điểm của đường ngang $y=m$ với đồ thị.',
      hint2: `Không tính hai mức cực trị $${lower}$ và $${upper}$ vì khi đó có tiếp xúc.`,
      estimatedSeconds: 150, variantGroup: 'ch1-cubic-levels-v2',
    },
    {
      ...functionBase(seed, 'Hàm bậc ba – tiếp xúc theo mức giá trị', ['logical-reasoning', 'problem-solving']),
      id: `VACT-CH1-CUBIC-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'Gọi $S$ là tổng các giá trị thực $m$ để phương trình $f(x)=m$ có đúng hai nghiệm thực phân biệt. Giá trị $S$ là',
      choices: numericChoices(2 * c, [lower + upper + 2, lower * upper, upper - lower]),
      answer: 'A',
      solution: `Có đúng hai nghiệm phân biệt khi đường $y=m$ tiếp xúc đồ thị tại một điểm cực trị và cắt nhánh còn lại một lần. Do đó $m=${lower}$ hoặc $m=${upper}$. Vậy $S=${lower}+${upper}=${2 * c}$.`,
      hint1: 'Hai nghiệm phân biệt xuất hiện ở hai “mức ranh giới” của trường hợp ba nghiệm.',
      hint2: 'Cộng hai tung độ cực trị.',
      estimatedSeconds: 145, variantGroup: 'ch1-cubic-double-root-v2',
    },
    {
      ...functionBase(seed, 'Hàm bậc ba – hệ số góc tiếp tuyến'),
      id: `VACT-CH1-CUBIC-${seed.id}-03`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Hệ số góc nhỏ nhất của mọi tiếp tuyến với đồ thị $y=f(x)$ bằng',
      choices: [option('A', `${minSlope - 3}`), option('B', `${minSlope + 3}`), option('C', `${minSlope}`), option('D', `${-minSlope}`)],
      answer: 'C',
      solution: `$f'(x)=3x^2-3\\cdot${r ** 2}$. Vì $3x^2\\ge0$, giá trị nhỏ nhất của $f'(x)$ là $-3\\cdot${r ** 2}=${minSlope}$, đạt tại $x=0$.`,
      hint1: 'Hệ số góc tiếp tuyến chính là đạo hàm.',
      hint2: 'Đạo hàm là một tam thức bậc hai có giá trị nhỏ nhất tại $x=0$.',
      estimatedSeconds: 105, variantGroup: 'ch1-cubic-slope-v2',
    },
  ]
}

function rationalQuestions(seed: Chapter1Seed): Question[] {
  const { a, b, q } = seed
  const lower = a + b - 2 * q
  const upper = a + b + 2 * q
  const lo = lower - 2
  const hi = upper + 2
  const noSolution = upper - lower - 1
  const horizontalDistance = `2${q === 1 ? '' : q}\\sqrt{5}`
  return [
    {
      ...functionBase(seed, 'Hàm phân thức – miền giá trị và tiệm cận', ['pattern-recognition', 'logical-reasoning', 'problem-solving']),
      id: `VACT-CH1-RATIONAL-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Khoảng giá trị bị khuyết',
      passageText: `Xét $g(x)=x${b >= 0 ? '+' : ''}${b}+\\dfrac{${q ** 2}}{x${a >= 0 ? '-' : '+'}${Math.abs(a)}}$, với $x\\ne${a}$.`,
      stem: `Có bao nhiêu số nguyên $m$ thuộc đoạn $[${lo};${hi}]$ để phương trình $g(x)=m$ vô nghiệm thực?`,
      choices: [option('A', `${noSolution + 1}`), option('B', `${noSolution - 1}`), option('C', `${noSolution + 2}`), option('D', `${noSolution}`)],
      answer: 'D',
      solution: `Ta có $g'(x)=1-\\dfrac{${q ** 2}}{(x-${a})^2}$. Hai mức cực trị là $${lower}$ và $${upper}$, nên miền giá trị là $(-\\infty;${lower}]\\cup[${upper};+\\infty)$. Phương trình vô nghiệm khi $${lower}<m<${upper}$, có $${upper}-${lower}-1=${noSolution}$ số nguyên.`,
      hint1: 'Dùng đạo hàm để tìm hai mức cực trị của hai nhánh.',
      hint2: 'Chỉ các mức nằm nghiêm ngặt giữa hai giá trị cực trị mới bị khuyết.',
      estimatedSeconds: 185, variantGroup: 'ch1-rational-range-v2',
    },
    {
      ...functionBase(seed, 'Hàm phân thức – số giao điểm theo mức giá trị', ['logical-reasoning', 'problem-solving']),
      id: `VACT-CH1-RATIONAL-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Có bao nhiêu số nguyên $m$ thuộc đoạn $[${lo};${hi}]$ để đường thẳng $y=m$ cắt đồ thị $y=g(x)$ tại đúng hai điểm phân biệt?`,
      choices: [option('A', '2'), option('B', '3'), option('C', '4'), option('D', '5')],
      answer: 'C',
      solution: `Đường $y=m$ cắt đồ thị tại hai điểm khi $m<${lower}$ hoặc $m>${upper}$. Trong đoạn $[${lo};${hi}]$, các giá trị là $${lo},${lo + 1},${hi - 1},${hi}$, gồm 4 giá trị. Hai mức $${lower}$ và $${upper}$ chỉ cho một tiếp điểm.`,
      hint1: 'Phân biệt “cắt hai điểm” với “tiếp xúc một điểm”.',
      hint2: `Xét phần ngoài khoảng $[${lower};${upper}]$ trong đoạn đã cho.`,
      estimatedSeconds: 160, variantGroup: 'ch1-rational-intersections-v2',
    },
    {
      ...functionBase(seed, 'Hàm phân thức – khoảng cách hai tiếp điểm'),
      id: `VACT-CH1-RATIONAL-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M2', type: 'single-choice',
      stem: 'Hai tiếp tuyến song song với trục hoành của đồ thị $y=g(x)$ tiếp xúc tại $A,B$. Độ dài $AB$ bằng',
      choices: [option('A', `$${horizontalDistance}$`), option('B', `${4 * q}`), option('C', `$${q}\\sqrt{5}$`), option('D', `${2 * q}`)],
      answer: 'A',
      solution: `$g'(x)=0$ khi $x=${a - q}$ hoặc $x=${a + q}$. Khi đó chênh lệch hoành độ là $2\\cdot${q}$, chênh lệch tung độ là $4\\cdot${q}$. Vậy $AB=\\sqrt{(2\\cdot${q})^2+(4\\cdot${q})^2}=${horizontalDistance}$.`,
      hint1: 'Giải $g\'(x)=0$ để tìm hai tiếp điểm.',
      hint2: 'Tính cả chênh lệch hoành độ và tung độ trước khi áp dụng công thức khoảng cách.',
      estimatedSeconds: 155, variantGroup: 'ch1-rational-horizontal-tangent-v2',
    },
  ]
}

function asymptoteParameterQuestions(seed: Chapter1Seed): Question[] {
  const u: number = -2
  const v: number = 1
  const n: number = 4
  const values = Array.from({ length: 9 }, (_, index) => index - 3)
  const oneAsymptoteValues = values.filter(m => m === u || m === v || m === n)
  const twoAsymptoteValues = values.filter(m => !oneAsymptoteValues.includes(m))
  return [
    {
      ...functionBase(seed, 'Tiệm cận đứng – triệt tiêu nhân tử và tham số', ['logical-reasoning', 'pattern-recognition', 'problem-solving']),
      id: `VACT-CH1-ASYMPTOTE-${seed.id}-01`, difficulty: 5, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Với mỗi $m\\in\\{-3,-2,\\ldots,5\\}$, xét $h_m(x)=\\dfrac{(x+2)(x-1)}{(x-m)(x-4)}$. Có bao nhiêu giá trị $m$ để đồ thị có đúng một tiệm cận đứng?`,
      choices: [option('A', `${oneAsymptoteValues.length - 1}`), option('B', `${oneAsymptoteValues.length}`), option('C', `${oneAsymptoteValues.length + 1}`), option('D', `${values.length - oneAsymptoteValues.length}`)],
      answer: 'B',
      solution: `Nếu $m$ khác $-2,1,4$ thì hai nghiệm mẫu số đều không bị triệt tiêu nên có hai tiệm cận đứng. Với $m=-2$ hoặc $m=1$, một nhân tử ở mẫu bị triệt tiêu; với $m=4$, mẫu có nghiệm kép $x=4$ nhưng vẫn chỉ cho một đường tiệm cận đứng. Vậy $m=-2,1,4$, có 3 giá trị.`,
      hint1: 'Đếm các nghiệm phân biệt của mẫu số còn lại sau rút gọn, không chỉ đếm bậc của mẫu.',
      hint2: 'Xét riêng ba khả năng $m=-2,1,4$.',
      estimatedSeconds: 215, variantGroup: 'ch1-asymptote-parameter-v2',
    },
    {
      ...functionBase(seed, 'Tiệm cận đứng – nhận dạng tham số', ['logical-reasoning', 'problem-solving']),
      id: `VACT-CH1-ASYMPTOTE-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'Trong cùng họ hàm số trên, có bao nhiêu giá trị nguyên $m\in\{-3,-2,\ldots,5\}$ để đồ thị có đúng hai tiệm cận đứng?',
      choices: [option('A', `${twoAsymptoteValues.length}`), option('B', `${twoAsymptoteValues.length - 1}`), option('C', `${twoAsymptoteValues.length + 1}`), option('D', `${oneAsymptoteValues.length}`)],
      answer: 'A',
      solution: `Có 9 giá trị $m$ trong tập đã cho. Ba giá trị $m=-2,1,4$ cho đúng một tiệm cận đứng; các giá trị còn lại đều để lại hai nghiệm mẫu số không bị triệt tiêu. Vì vậy có $9-3=${twoAsymptoteValues.length}$ giá trị.`,
      hint1: 'Dùng phần phân loại ở câu trước thay vì thử lại từng giá trị.',
      hint2: 'Tổng số giá trị tham số là 9.',
      estimatedSeconds: 135, variantGroup: 'ch1-asymptote-parameter-v2',
    },
  ]
}

function discreteProfitQuestions(seed: Chapter1Seed): Question[] {
  const { profitVertex: h, profitStep: d, profitScale: k } = seed
  const lower = h - (h % d)
  const upper = lower + d
  const best = Math.abs(lower - h) <= Math.abs(upper - h) ? lower : upper
  const other = best === lower ? upper : lower
  const loss = k * (best - h) ** 2
  return [
    {
      ...modelBase(seed, 'Tối ưu lợi nhuận – ràng buộc rời rạc'),
      id: `VACT-CH1-PROFIT-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Kế hoạch theo lô',
      passageText: `Lợi nhuận (triệu đồng) khi sản xuất $x$ trăm sản phẩm được mô hình hóa bởi $P(x)=-${k}(x-${h})^2+900$. Xưởng chỉ nhận các lô có $x$ là bội dương của ${d}.`,
      stem: 'Số trăm sản phẩm nào cần chọn để lợi nhuận lớn nhất?',
      choices: numericChoices(best, [lower, upper, h]),
      answer: 'A',
      solution: `Trên miền liên tục, lợi nhuận lớn nhất tại đỉnh $x=${h}$. Do $x$ phải là bội của ${d}, chỉ cần so sánh hai bội kề $${lower}$ và $${upper}$. Giá trị gần $${h}$ hơn là $${best}$, nên cho lợi nhuận lớn hơn.`,
      hint1: 'Đỉnh của parabol cho lời giải liên tục, chưa chắc thỏa ràng buộc theo lô.',
      hint2: `Chỉ hai bội của ${d} kề ${h} cần so sánh.`,
      estimatedSeconds: 160, variantGroup: 'ch1-discrete-profit-v2',
    },
    {
      ...modelBase(seed, 'Tối ưu lợi nhuận – chi phí của ràng buộc theo lô'),
      id: `VACT-CH1-PROFIT-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'So với mức lợi nhuận lý tưởng khi được chọn $x$ liên tục, ràng buộc theo lô làm giảm ít nhất bao nhiêu triệu đồng?',
      choices: numericChoices(loss, [loss + k, Math.max(0, loss - k), loss * 2]),
      answer: 'A',
      solution: `Độ giảm so với đỉnh tại $x=${h}$ là $${k}(x-${h})^2$. Với lựa chọn tốt nhất $x=${best}$, độ giảm là $${k}\\cdot(${best}-${h})^2=${loss}$ (triệu đồng).`,
      hint1: 'Viết lợi nhuận dưới dạng đỉnh trừ đi một số không âm.',
      hint2: `Thay $x=${best}$ vào phần $${k}(x-${h})^2$.`,
      estimatedSeconds: 145, variantGroup: 'ch1-discrete-profit-v2',
    },
  ]
}

function openBoxQuestions(seed: Chapter1Seed): Question[] {
  const s = seed.boxSide
  const x = s / 6
  const volume = x * (s - 2 * x) ** 2
  return [
    {
      ...modelBase(seed, 'Tối ưu thể tích hộp không nắp'),
      id: `VACT-CH1-OPENBOX-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Thiết kế hộp đựng',
      passageText: `Từ tấm bìa hình vuông cạnh ${s} cm, cắt ở mỗi góc một hình vuông cạnh $x$ cm rồi gấp thành hộp không nắp.`,
      assets: [{ assetId: 'ch1-open-box-net-v1', kind: 'diagram', placement: 'passage', alt: 'Sơ đồ cắt bốn góc hình vuông cạnh x và gấp thành hộp không nắp.' }],
      stem: 'Cần chọn $x$ bằng bao nhiêu để thể tích hộp lớn nhất?',
      choices: [option('A', `${x / 2} cm`), option('B', `${x} cm`), option('C', `${2 * x} cm`), option('D', `${s / 4} cm`)],
      answer: 'B',
      solution: `Thể tích là $V(x)=x(${s}-2x)^2$, với $0<x<${s / 2}$. Ta có $V'(x)=(${s}-2x)(${s}-6x)$. Nghiệm nội $x=${s}/6=${x}$ cho cực đại vì thể tích tiến về 0 ở hai đầu miền.`,
      hint1: 'Sau khi gấp, đáy có cạnh $s-2x$ và chiều cao là $x$.',
      hint2: 'Đạo hàm cần được phân tích nhân tử để không bỏ nghiệm biên.',
      estimatedSeconds: 185, variantGroup: 'ch1-open-box-v2',
    },
    {
      ...modelBase(seed, 'Tối ưu thể tích hộp không nắp'),
      id: `VACT-CH1-OPENBOX-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      assets: [{ assetId: 'ch1-open-box-net-v1', kind: 'diagram', placement: 'stem', alt: 'Sơ đồ cắt bốn góc hình vuông cạnh x và gấp thành hộp không nắp.' }],
      stem: 'Thể tích lớn nhất của hộp bằng',
      choices: [option('A', `${volume / 2} cm³`), option('B', `${volume + s} cm³`), option('C', `${volume} cm³`), option('D', `${volume * 2} cm³`)],
      answer: 'C',
      solution: `Tại $x=${x}$, đáy có cạnh $${s}-2\\cdot${x}=${s - 2 * x}$ cm. Vì vậy $V_{max}=${x}\\cdot${s - 2 * x}^2=${volume}$ cm³.`,
      hint1: 'Dùng giá trị $x$ tối ưu vừa tìm được.',
      hint2: 'Không nhầm cạnh đáy với cạnh tấm bìa ban đầu.',
      estimatedSeconds: 120, variantGroup: 'ch1-open-box-v2',
    },
  ]
}

function pageQuestions(seed: Chapter1Seed): Question[] {
  const { marginP: p, marginQ: q, marginK: k } = seed
  const innerW = p * k
  const innerH = q * k
  const innerArea = innerW * innerH
  const pageW = innerW + 2 * p
  const pageH = innerH + 2 * q
  const paperArea = pageW * pageH
  return [
    {
      ...modelBase(seed, 'Tối ưu khổ giấy – diện tích in cố định'),
      id: `VACT-CH1-PAGE-${seed.id}-01`, difficulty: 5, cognitiveLevel: 'M3', type: 'single-choice',
      passageTitle: 'Thiết kế trang in',
      passageText: `Một trang có vùng in hình chữ nhật diện tích ${innerArea} cm². Lề trái-phải đều ${p} cm, lề trên-dưới đều ${q} cm.`,
      stem: 'Để diện tích tờ giấy nhỏ nhất, chiều rộng của vùng in cần bằng',
      choices: numericChoices(innerW, [innerH, pageW, k], ' cm'),
      answer: 'A',
      solution: `Gọi chiều rộng vùng in là $w$, chiều cao là $${innerArea}/w$. Diện tích giấy là $A(w)=(w+${2 * p})(${innerArea}/w+${2 * q})$. Suy ra $A'(w)=${2 * q}-\\dfrac{${2 * p * innerArea}}{w^2}$. Điều kiện $A'(w)=0$ cho $w^2=${p * innerArea}/${q}=${innerW ** 2}$, nên $w=${innerW}$ cm.`,
      hint1: 'Đặt một cạnh vùng in là biến; cạnh còn lại suy ra từ diện tích cố định.',
      hint2: 'Lấy nghiệm dương sau khi giải phương trình đạo hàm bằng 0.',
      estimatedSeconds: 230, variantGroup: 'ch1-page-margin-v2',
    },
    {
      ...modelBase(seed, 'Tối ưu khổ giấy – diện tích in cố định'),
      id: `VACT-CH1-PAGE-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: 'Diện tích nhỏ nhất của tờ giấy là',
      choices: [option('A', `${innerArea} cm²`), option('B', `${paperArea} cm²`), option('C', `${paperArea + 2 * p * q} cm²`), option('D', `${pageW + pageH} cm²`)],
      answer: 'B',
      solution: `Khi vùng in tối ưu có kích thước ${innerW} cm × ${innerH} cm, tờ giấy có kích thước $(${innerW}+${2 * p})\\times(${innerH}+${2 * q})=${pageW}\\times${pageH}$. Do đó diện tích nhỏ nhất là ${paperArea} cm².`,
      hint1: 'Cộng lề ở cả hai phía của mỗi chiều.',
      hint2: `Kích thước tờ giấy là ${pageW} cm × ${pageH} cm.`,
      estimatedSeconds: 130, variantGroup: 'ch1-page-margin-v2',
    },
  ]
}

function tangentQuestions(seed: Chapter1Seed): Question[] {
  const { r, c } = seed
  const t = r + 1
  const slope = 3 * (t * t - r * r)
  const intercept = c - 2 * t ** 3
  const yLow = c - 3 * r ** 3
  const yHigh = c - 2 * r ** 3
  const yPoint = (yLow + yHigh) / 2
  return [
    {
      ...functionBase(seed, 'Tiếp tuyến hàm bậc ba – tham số hoành độ', ['quantitative-reasoning', 'problem-solving']),
      id: `VACT-CH1-TANGENT-${seed.id}-01`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Cho $f(x)=x^3-3\\cdot${r ** 2}x${c >= 0 ? '+' : ''}${c}$. Tiếp tuyến của đồ thị tại điểm có hoành độ $t=${t}$ có phương trình là`,
      choices: [option('A', `$y=${slope}x${intercept >= 0 ? '+' : ''}${intercept}$`), option('B', `$y=${slope}x${c >= 0 ? '+' : ''}${c}$`), option('C', `$y=${-slope}x${intercept >= 0 ? '+' : ''}${intercept}$`), option('D', `$y=${slope + 1}x${intercept >= 0 ? '+' : ''}${intercept}$`)],
      answer: 'A',
      solution: `Ta có $f'(${t})=3(${t}^2-${r ** 2})=${slope}$. Tung độ gốc của tiếp tuyến là $f(t)-tf'(t)=${c}-2\\cdot${t}^3=${intercept}$. Vậy tiếp tuyến là $y=${slope}x${intercept >= 0 ? '+' : ''}${intercept}$.`,
      hint1: 'Dùng dạng $y=f\'(t)(x-t)+f(t)$.',
      hint2: 'Có thể tính tung độ gốc bằng $f(t)-t f\'(t)$.',
      estimatedSeconds: 175, variantGroup: 'ch1-tangent-cubic-v2',
    },
    {
      ...functionBase(seed, 'Tiếp tuyến hàm bậc ba – đếm số tiếp tuyến qua điểm', ['logical-reasoning', 'pattern-recognition', 'problem-solving']),
      id: `VACT-CH1-TANGENT-${seed.id}-02`, difficulty: 5, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Có bao nhiêu tiếp tuyến của đồ thị $y=f(x)$ đi qua điểm $P(${r};${yPoint})$?`,
      choices: [option('A', '1'), option('B', '2'), option('C', '3'), option('D', '4')],
      answer: 'C',
      solution: `Gọi tiếp điểm có hoành độ $z$. Giá trị tung độ của tiếp tuyến tại $x=${r}$ là $H(z)=f(z)+f'(z)(${r}-z)$. Hàm $H$ có hai mức cực trị $H(0)=${yLow}$ và $H(${r})=${yHigh}$. Vì $${yPoint}$ nằm nghiêm ngặt giữa $${yLow}$ và $${yHigh}$, phương trình $H(z)=${yPoint}$ có ba nghiệm thực phân biệt, tương ứng với 3 tiếp tuyến.`,
      hint1: 'Đừng cố đoán từ hình: tham số hóa tiếp tuyến theo hoành độ tiếp điểm.',
      hint2: `Tại $x=${r}$, hàm phụ có hai mức $${yLow}$ và $${yHigh}$; điểm P chọn ở giữa hai mức này.`,
      estimatedSeconds: 260, variantGroup: 'ch1-tangent-through-point-v2',
    },
  ]
}

/** 16 high-quality, source-derived patterns per seed; all numbers and contexts are original. */
export function chapter1FunctionQuestions(seed: Chapter1Seed): Question[] {
  return [
    ...cubicQuestions(seed),
    ...rationalQuestions(seed),
    ...asymptoteParameterQuestions(seed),
    ...discreteProfitQuestions(seed),
    ...openBoxQuestions(seed),
    ...pageQuestions(seed),
    ...tangentQuestions(seed),
  ]
}
